import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCanWrite } from "@/app/auth";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { beijingMinute, currentCost, firstPick, groupChannels, groupOps, memberRows, pbRow, type MemberRow } from "@/app/groupPerformance";
import { channelGroups, channels, costPrices, currencyRates, STRATEGY } from "@/app/master";
import { nameOf, useChannelHealth, useList, usePrimaryBackup } from "@/app/queries";
import { Alert, Button, EmptyState, FILTER_W, formatTime, PageHeader, Select, TableWrap, Toolbar, ToolbarCount } from "@/ui";
import { ChannelGroupDrawer } from "./ChannelGroupDrawer";
import { GroupMembersTable } from "./GroupMembersTable";
import { GroupStrategyPanel } from "./GroupStrategyPanel";
import { usePriceClock } from "./priceList";

// 比较成本的策略才拉成本价与汇率
const COSTED = ["least_cost", "quality_based"];

/** 通道组合详情 /channel-groups/{id}：成员的实际表现与当前状态，按策略的特点展示现在怎么选、选到谁（docs/10 第 3 节）。
 * 指标是通道 × 运营商的快照（与通道健康页同一份，每 10 秒刷新），不按组区分。 */
export function ChannelGroupDetail() {
  const { id = "" } = useParams();
  const groupID = Number(id);
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const [op, setOp] = useState("");
  const [editing, setEditing] = useState(false);
  const now = usePriceClock();
  const groups = useList("channel-groups", channelGroups);
  const channelList = useList("channels", channels).data;
  const health = useChannelHealth();
  const pb = usePrimaryBackup();
  const g = groups.data?.find((x) => x.id === groupID);
  const costed = COSTED.includes(g?.strategy ?? "");
  const prices = useQuery({ queryKey: ["cost-prices", {}], queryFn: () => costPrices.list(), enabled: costed });
  const rates = useQuery({ queryKey: ["currency-rates", {}], queryFn: () => currencyRates.list(), enabled: costed });
  const back = <Button onClick={() => navigate("/channel-groups")}>返回</Button>;

  if (!g) {
    return (
      <>
        <PageHeader title="通道组合" actions={back} />
        {groups.error ? <Alert tone="danger" title="加载失败">{groups.error.message}</Alert> : groups.isPending ? <TableWrap loading /> : <EmptyState title="通道组合不存在">{`没有编号为 ${id} 的通道组合，可能已删除。`}</EmptyState>}
      </>
    );
  }

  const items = health.data?.items;
  const ops = groupOps(items, groupChannels(g.id, groups.data));
  const opKey = ops.includes(op) ? op : null; // 换了组合或运营商没流量了，回到全部
  const rows = memberRows(g, groups.data, channelList, { items, op: opKey, minute: beijingMinute(now) });
  const name = (r: MemberRow) => (r.member.group_id ? nameOf(groups.data, r.member.group_id) : nameOf(channelList, r.member.channel_id!));
  const channelName = (x: number) => nameOf(channelList, x);
  const costs = opKey && costed && prices.data ? rows.map((r) => currentCost(prices.data, rates.data ?? [], r.member.channel_id!, opKey, now)) : undefined;
  // 成本从低到高，没配或换算不了的排最后，同价按 priority（rows 已按 priority 排好，排序是稳定的）
  const costRank = costs && rows.map((_, i) => i).sort((a, b) => (costs[a]?.base ?? Infinity) - (costs[b]?.base ?? Infinity));
  // 选路与熔断都按 通道 × 运营商 判定，只有选了运营商才推算首选；全部运营商合并后的"最差"状态不代表任何一个运营商上的选择
  const pick = opKey === null ? -1 : g.strategy === "least_cost" ? (costRank ? (costRank[firstPick(costRank.map((i) => rows[i]))] ?? -1) : -1) : firstPick(rows);
  const tpsTotal = rows.reduce((s, r) => s + (r.stats?.tps ?? 0), 0);
  const actualShare = rows.map((r) => (tpsTotal ? (r.stats?.tps ?? 0) / tpsTotal : null));
  // 快照过期时 worker 读不到主备状态，一律按主用走主通道（docs/10 第 9 节）
  const pbStale = !!pb.data && !pb.data.built_at;
  const pbState = opKey && !pbStale ? pbRow(pb.data, g.id, opKey) : undefined;

  return (
    <>
      <PageHeader title={<>{g.name} <EntityStatusPill status={g.status} /></>}
        description={`策略：${STRATEGY[g.strategy] ?? g.strategy}。指标按通道 × 运营商统计全部流量（与通道健康同一份快照），含通道在别的组合里的流量。`}
        actions={<>{back}<Button onClick={() => setEditing(true)}>{canWrite ? "编辑" : "查看配置"}</Button></>} />
      {health.error && <Alert tone="danger" title="加载失败">{health.error.message}</Alert>}
      {health.data && !health.data.built_at && <Alert tone="warning" title="没有指标快照">metrics 角色没有在运行：路由拿不到指标，熔断与主备状态都不会推进。</Alert>}
      {g.status === "disabled" && <Alert tone="warning" title="组合已停用">路由到这个组合的消息按无路由失败；下面是成员本身的表现。</Alert>}
      <Toolbar right={health.data?.built_at && <ToolbarCount>快照 {formatTime(health.data.built_at)}</ToolbarCount>}>
        <Select size="sm" width={FILTER_W.select} ariaLabel="运营商" options={[{ value: "", label: "全部运营商" }, ...ops.map((o) => ({ value: o, label: o }))]} value={opKey ?? ""} onChange={(v) => setOp(v ?? "")} />
      </Toolbar>
      <div className="ax-detail">
        <GroupStrategyPanel group={g} rows={rows} op={opKey} now={now} name={name} channel={channelName} pb={pb.data} pbStale={pbStale} ops={ops} costRank={costRank} actualShare={actualShare} />
        <GroupMembersTable group={g} rows={rows} op={opKey} name={name} pick={pick} costs={costs} actualShare={actualShare} pbState={pbState} />
      </div>
      {editing && <ChannelGroupDrawer row={g} groups={groups.data} channels={channelList} onClose={() => setEditing(false)} onDeleted={() => navigate("/channel-groups")} />}
    </>
  );
}
